import { pwnedPassword } from 'hibp'

import type { PasswordStrength } from 'types/business'

const passwordStrengthMinChars: {
  [key: string]: number
} = {
  standard: 6,
  strong: 8,
  extra_strong: 12,
}

// might be better to user something like
// https://github.com/andreruffert/number-words
const passwordStrengthMinCharsInWords: {
  [key: string]: string
} = {
  standard: 'six',
  strong: 'eight',
  extra_strong: 'twelve',
}

/**
 * Narrows a value to a password strength level.
 *
 * Server password rejections carry the business's required strength in the
 * field a human-readable detail would otherwise occupy, so callers have to be
 * able to tell metadata from a message.
 */
export const isPasswordStrengthError = (
  value: unknown,
): value is PasswordStrength =>
  typeof value === 'string' && value in passwordStrengthMinChars

/**
 * Gets the minimum character count required for a given password strength level.
 *
 * @param passwordStrength - The password strength level ('standard', 'strong', 'extra_strong')
 * @returns The minimum number of characters required
 *
 * @example
 * ```typescript
 * getPasswordStrengthMinimumChars('standard') // returns 6
 * getPasswordStrengthMinimumChars('strong') // returns 8
 * getPasswordStrengthMinimumChars('extra_strong') // returns 12
 * ```
 */
export const getPasswordStrengthMinimumChars = (
  passwordStrength: PasswordStrength,
): number => {
  return (
    passwordStrengthMinChars[passwordStrength] ??
    passwordStrengthMinChars['standard'] ??
    6
  )
}

/**
 * Gets the minimum character count as a word for a given password strength level.
 *
 * @param passwordStrength - The password strength level
 * @returns The minimum character count as a word (e.g., "six", "eight")
 *
 * @example
 * ```typescript
 * getPasswordStrengthMinimumCharsInWords('standard') // returns 'six'
 * getPasswordStrengthMinimumCharsInWords('strong') // returns 'eight'
 * ```
 */
export const getPasswordStrengthMinimumCharsInWords = (
  passwordStrength: PasswordStrength,
): string => {
  return (
    passwordStrengthMinCharsInWords[passwordStrength] ??
    passwordStrengthMinCharsInWords['standard'] ??
    'six'
  )
}

/**
 * Checks if a password meets security requirements for the given strength level.
 *
 * Validates that the password meets minimum length requirements and contains
 * both letters and numbers.
 *
 * @param password - The password to validate
 * @param passwordStrength - The required strength level
 * @returns True if password meets security requirements, false otherwise
 *
 * @example
 * ```typescript
 * checkPasswordSecurity('abc123', 'standard') // returns true (6+ chars, letters + numbers)
 * checkPasswordSecurity('abc', 'standard') // returns false (too short)
 * checkPasswordSecurity('abcdef', 'standard') // returns false (no numbers)
 * ```
 */
export function checkPasswordSecurity(
  password: string,
  passwordStrength: PasswordStrength,
): boolean {
  const minLength = getPasswordStrengthMinimumChars(passwordStrength)
  if (!password || password.length < minLength) return false
  return /\d/.test(password) && /[A-Za-z_]/.test(password)
}

/**
 * Checks if a password has been found in known data breaches.
 *
 * Uses the Have I Been Pwned API to check if the password appears
 * in known security breaches.
 *
 * @param password - The password to check
 * @returns Promise resolving to the number of times the password was found in breaches
 *
 * @example
 * ```typescript
 * const breachCount = await checkPasswordForBreaches('password123')
 * if (breachCount > 0) {
 *   console.log(`Password found in ${breachCount} breaches`)
 * }
 * ```
 */
export async function checkPasswordForBreaches(
  password: string,
): Promise<number> {
  return await pwnedPassword(password)
}

/**
 * Calculates a password strength score from 0 to 100.
 *
 * Scores based on length, character variety, and uniqueness of characters.
 * A score of 50+ is considered good. Bonus points for meeting recommended length.
 *
 * @param pass - The password to score
 * @param passwordStrength - The target strength level for bonus scoring
 * @returns Password strength score (0-100, where 50+ is good)
 *
 * @example
 * ```typescript
 * scorePassword('abc', 'standard') // returns low score
 * scorePassword('Abc123!@#', 'standard') // returns high score (variety + length)
 * scorePassword('MySecureP@ssw0rd!', 'strong') // returns very high score
 * ```
 */
export function scorePassword(
  pass: string,
  passwordStrength: PasswordStrength,
): number {
  let score = 0
  if (!pass || pass === '') return score

  // award every unique letter until 5 repetitions
  const letters: Record<string, any> = {}
  for (let i = 0; i < pass.length; i++) {
    const char = pass[i]
    if (char) {
      letters[char] = (letters[char] || 0) + 1
      score += 2.0 / letters[char]
    }
  }

  // bonus points for mixing it up
  const variations = {
    digits: /\d/.test(pass),
    lower: /[a-z]/.test(pass),
    upper: /[A-Z]/.test(pass),
    nonWords: /\W/.test(pass),
  } as const

  let variationCount = 0
  for (const check in variations) {
    // eslint-disable-next-line no-prototype-builtins
    if (variations.hasOwnProperty(check)) {
      variationCount += variations[check] ? 1 : 0
    }
  }

  score += (variationCount - 1) * 10

  const recommendedLen = getPasswordStrengthMinimumChars(passwordStrength) + 2
  if (pass.length >= recommendedLen) {
    score += 20
  }

  if (score > 100) score = 100

  return score
}
